import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, provideZonelessChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling } from '@angular/router';
import { definePreset, palette } from '@primeuix/themes';
// Compat preset: the portal keeps a 14px root font; PrimeNG 22 presets assume 16px (re-baseline is a separate task).
import AuraCompat from '@primeuix/themes/aura-compat';
import { environment } from '../../environments/environment';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { cloudAppRoutes } from './cloud-app.routes';
import { authInterceptor } from './auth.interceptor';
import { PING_ROUTE, PingRouteFactory } from 'src/api/ping-route';
import { DeviceContextService } from './pages/device/device-context.service';
import { provideCloudInsightPanels } from './insights/insight-panels.providers';

/** proAV brand green. The single source for the cloud portal's primary colour. */
export const BRAND_PRIMARY = '#7ab800';

/**
 * Neutral grey surface ramp used for both colour schemes. Aura's defaults are
 * slate (light) and zinc (dark), which read blue; the portal has always been
 * grey, previously applied at runtime by the theme configurator.
 */
export const BRAND_SURFACE = {
    0: '#ffffff',
    50: '{gray.50}',
    100: '{gray.100}',
    200: '{gray.200}',
    300: '{gray.300}',
    400: '{gray.400}',
    500: '{gray.500}',
    600: '{gray.600}',
    700: '{gray.700}',
    800: '{gray.800}',
    900: '{gray.900}',
    950: '{gray.950}'
} as const;

/**
 * Cloud portal theme: Aura with the primary palette generated from the brand
 * colour. The portal is locked to this preset (no per-user primary/surface
 * pickers, see LayoutService.allowThemeCustomisation) so every operator sees
 * the same UI and the status palette in `_common.scss` is tuned once against it.
 */
export const ProAvPreset = definePreset(AuraCompat, {
    semantic: {
        primary: palette(BRAND_PRIMARY),
        colorScheme: {
            light: { surface: BRAND_SURFACE },
            dark: { surface: BRAND_SURFACE }
        }
    }
});

export const cloudAppConfig: ApplicationConfig = {
    providers: [
        provideZonelessChangeDetection(),
        provideRouter(cloudAppRoutes, withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }), withEnabledBlockingInitialNavigation()),
        provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
        provideAnimationsAsync(),
        providePrimeNG({ theme: { preset: ProAvPreset, options: { darkModeSelector: '.app-dark' } }, license: environment.primeUiLicense }),
        MessageService,
        ...provideCloudInsightPanels(),
        {
            // The ping page lives under the device being viewed. Pages that list several
            // devices pass the row's id; otherwise fall back to the device currently open.
            provide: PING_ROUTE,
            useFactory: (): PingRouteFactory => {
                const deviceCtx = inject(DeviceContextService);
                return (host: string, deviceId?: string) => {
                    const id = deviceId ?? deviceCtx.device()?.id;
                    if (!id) return null;
                    return { link: ['/device', id, 'ping'], queryParams: { host, autorun: 1 } };
                };
            }
        }
    ]
};
